import React, { useEffect, useState, useCallback } from "react";
import { usePostForm } from "./contexts/PostFormContext";
import { RTEField } from "./components/RTEField";
import { useCategories } from "../../../lib/firebase/category/read";
import { useAuthors } from "../../../lib/firebase/author/read";
import { useSearchParams } from "react-router-dom";
import { Timestamp } from "firebase/firestore";

function FormPage() {
  const [searchParams] = useSearchParams();
  const updatePostId = searchParams.get("id");
  const collectionParam = searchParams.get("collection") || "posts";

  const {
    data,
    isLoading,
    error,
    isDone,
    handleData,
    handleCreate,
    handleUpdate,
    handleDelete,
    image,
    setImage,
    pdfFile,
    setPdfFile,
    fetchData,
  } = usePostForm();

  const [postCategory, setPostCategory] = useState(
    collectionParam === "magazines" ? "magazine" : "post"
  );

  const fetchPostData = useCallback(() => {
    if (updatePostId) {
      const collectionName =
        postCategory === "magazine" ? "magazines" : "posts";
      fetchData(updatePostId, collectionName);
    }
  }, [updatePostId, postCategory, fetchData]);

  useEffect(() => {
    fetchPostData();
  }, [fetchPostData]);

  const formatDateForInput = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toISOString().split("T")[0];
  };

  const getCurrentDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const updatedData = { ...data, productType: postCategory };
    const collectionName = postCategory === "magazine" ? "magazines" : "posts";
    console.log("Submitting:", {
      data: updatedData,
      image,
      pdfFile,
      collectionName,
    });
    if (updatePostId) {
      handleUpdate(collectionName, updatedData);
    } else {
      handleCreate(collectionName, updatedData);
    }
  };

  return (
    <main className="py-2">
      <div className="mb-3 d-flex justify-content-between align-items-center">
        {updatePostId && (
          <span className="badge bg-warning text-dark">Update</span>
        )}
        {!updatePostId && (
          <span className="badge bg-success text-white">Create</span>
        )}
        <h1 className="h4">Posts | Form</h1>
      </div>

      <section>
        <form
          onSubmit={handleFormSubmit}
          className="p-4 bg-light rounded shadow-sm"
        >
          <div className="mb-3">
            <label className="form-label">Meta Title</label>
            <input
              className="form-control"
              placeholder="Enter Meta Title"
              type="text"
              onChange={(e) => handleData("metaTitle", e.target.value)}
              value={data?.metaTitle || ""}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Meta Description</label>
            <input
              className="form-control"
              placeholder="Enter Meta Description"
              type="text"
              onChange={(e) => handleData("metaDescription", e.target.value)}
              value={data?.metaDescription || ""}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">
              Title <span className="text-danger">*</span>
            </label>
            <input
              className="form-control"
              placeholder="Enter Title"
              type="text"
              onChange={(e) => handleData("title", e.target.value)}
              value={data?.title || ""}
              required
            />
          </div>
          <div className="mb-3">
            <label className="form-label">
              Slug <span className="text-danger">*</span>
            </label>
            <input
              className="form-control"
              placeholder="Enter Slug"
              type="text"
              disabled={updatePostId}
              onChange={(e) => handleData("slug", e.target.value)}
              value={data?.slug || ""}
              required
            />
          </div>
          <div className="mb-3">
            <label className="form-label">
              Date <span className="text-danger">*</span>
            </label>
            <input
              className="form-control"
              type="date"
              onChange={(e) => {
                const date = new Date(e.target.value);
                handleData("timestamp", Timestamp.fromDate(date));
              }}
              value={formatDateForInput(data?.timestamp)}
              max={getCurrentDate()}
              required
            />
          </div>
          <div className="mb-3">
            <label className="form-label">
              Product Type <span className="text-danger">*</span>
            </label>
            <select
              className="form-select"
              value={postCategory}
              onChange={(e) => setPostCategory(e.target.value)}
              required
            >
              <option value="post">Post</option>
              <option value="magazine">Magazine</option>
            </select>
          </div>
          {postCategory === "magazine" && <SelectPublishedCategoryField />}
          <SelectCategoryField />
          <SelectAuthorField />
          {data?.imageURL && (
            <div className="mb-3">
              <img
                className="img-fluid"
                src={data?.imageURL}
                alt="ImagePreview"
                style={{ maxWidth: "50%" }}
              />
            </div>
          )}
          {image && (
            <div className="mb-3">
              <img
                className="img-fluid"
                src={URL.createObjectURL(image)}
                alt="ImagePreview"
              />
            </div>
          )}
          <div className="mb-3">
            <label className="form-label">
              Image{" "}
              {postCategory === "post" && (
                <span className="text-danger">*</span>
              )}
            </label>
            <input
              className="form-control"
              type="file"
              accept="image/*"
              onChange={(e) => setImage(e.target.files[0])}
              required={postCategory === "post" && !updatePostId}
            />
          </div>
          {postCategory === "post" && <RTEField />}
          {postCategory === "magazine" && (
            <div className="mb-3">
              <label className="form-label">
                Upload Magazine PDF <span className="text-danger">*</span>
              </label>
              <input
                className="form-control"
                type="file"
                accept="application/pdf"
                onChange={(e) => setPdfFile(e.target.files[0])}
                required={!updatePostId}
              />
              {data?.pdfURL && (
                <div className="mt-2">
                  <a
                    href={data.pdfURL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Current PDF
                  </a>
                </div>
              )}
            </div>
          )}
          {error && <div className="text-danger mb-3">{error}</div>}
          <div className="d-flex gap-3">
            <button
              type="submit"
              disabled={isLoading}
              className="btn btn-primary w-100"
            >
              {isLoading && updatePostId
                ? "Updating..."
                : isLoading
                ? "Creating..."
                : updatePostId
                ? "Update"
                : "Create"}
            </button>
            {updatePostId && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  handleDelete(
                    updatePostId,
                    postCategory === "magazine" ? "magazines" : "posts"
                  );
                }}
                disabled={isLoading}
                className="btn btn-danger w-100"
              >
                {isLoading ? "Deleting..." : "Delete"}
              </button>
            )}
          </div>
          {isDone && (
            <div className="text-success text-center mt-3">
              Successfully {updatePostId ? "Updated" : "Created"}!
            </div>
          )}
        </form>
      </section>
    </main>
  );
}

function SelectCategoryField() {
  const { data, handleData } = usePostForm();
  const { data: categories } = useCategories();
  return (
    <div className="mb-3">
      <label className="form-label">
        Category <span className="text-danger">*</span>
      </label>
      <select
        className="form-select"
        name="category"
        id="category"
        value={data?.categoryId || ""}
        onChange={(e) => handleData("categoryId", e.target.value)}
        required
      >
        <option value="">Select Category</option>
        {categories &&
          categories?.map((item) => (
            <option key={item?.id} value={item?.id}>
              {item?.name}
            </option>
          ))}
      </select>
    </div>
  );
}

function SelectPublishedCategoryField() {
  const { data, handleData } = usePostForm();
  const { data: categories } = useCategories(); // Reusing categories

  return (
    <div className="mb-3">
      <label className="form-label">
        Published By <span className="text-danger">*</span>
      </label>
      <select
        className="form-select"
        name="publishedBy"
        id="publishedBy"
        value={data?.publishedBy || ""}
        onChange={(e) => handleData("publishedBy", e.target.value)}
        required
      >
        <option value="">Select Publisher</option>
        {categories &&
          categories?.map((item) => (
            <option key={item?.id} value={item?.name}>
              {item?.name}
            </option>
          ))}
      </select>
    </div>
  );
}

function SelectAuthorField() {
  const { data, handleData } = usePostForm();
  const { data: authors } = useAuthors();

  const handleAuthorChange = (e) => {
    const selectedAuthorId = e.target.value;
    handleData("authorId", selectedAuthorId);

    const selectedAuthor = authors.find(
      (author) => author.id === selectedAuthorId
    );
    if (selectedAuthor) {
      handleData("authorName", selectedAuthor.name);
    } else {
      handleData("authorName", "");
    }
  };

  return (
    <div className="mb-3">
      <label className="form-label">
        Authors <span className="text-danger">*</span>
      </label>
      <select
        className="form-select"
        name="authorId"
        id="authorId"
        value={data?.authorId || ""}
        onChange={handleAuthorChange}
        required
      >
        <option value="">Select Author</option>
        {authors &&
          authors?.map((item) => (
            <option key={item?.id} value={item?.id}>
              {item?.name}
            </option>
          ))}
      </select>
    </div>
  );
}

export default FormPage;
